import 'react-toastify/dist/ReactToastify.css';
import 'react-circular-progressbar/dist/styles.css';
import 'react-responsive-carousel/lib/styles/carousel.min.css';
import '@travelwin/core/src/styles/variables.css';
import '@/index.scss';

import { Capacitor } from '@capacitor/core';
import { SplashScreen } from '@capacitor/splash-screen';
import * as Sentry from '@sentry/react-router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import type { Redirect } from '@travelwin/cms/types';
import {
  Button,
  type Config,
  DesignSystemProvider,
  getBrandFontsUrl,
  getBrandVariables,
  isDefaultBrand,
  MockConfigProvider,
  SessionProvider,
  setDefaultDateFnsLocale,
  SLHeaders,
  type SUPPORTED_LOCALE,
} from '@travelwin/core';
import { useEffect, useState } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { Trans, useTranslation } from 'react-i18next';
import {
  data,
  isRouteErrorResponse,
  Links,
  Meta,
  Outlet,
  redirect,
  Scripts,
  ScrollRestoration,
  useRouteLoaderData,
} from 'react-router';
import { useChangeLanguage } from 'remix-i18next/react';
import { v4 as uuidv4 } from 'uuid';

import { configureAmplify } from '@/amplify.config';
import { AnalyticsProvider } from '@/analytics';
import { DiscountCodeManager } from '@/components/DiscountPopup/DiscountCodeManager';
import { ApiCompatibilityErrorScreen } from '@/components/GlobalErrorBoundaryFallback/ApiCompatibilityErrorScreen';
import GlobalErrorScreen from '@/components/GlobalErrorBoundaryFallback/components/GlobalErrorScreen/GlobalErrorScreen';
import GlobalErrorBoundaryFallback from '@/components/GlobalErrorBoundaryFallback/GlobalErrorBoundaryFallback';
import LoadingBarProvider from '@/components/LoadingBar';
import { MetaPixelTracker } from '@/components/MetaPixel';
import { TikTokPixelTracker } from '@/components/TikTokPixel';
import { CMSProvider } from '@/context/CMS';
import { ConnectionProvider } from '@/context/Connection';
import { ConsentProvider } from '@/context/Consent';
import { CurrencyProvider } from '@/context/Currency';
import EuiccManager from '@/context/EuiccManager';
import { UserProvider } from '@/context/User';
import { getConfig } from '@/services/api';
import { type ShopBrand, shopBrands } from '@/utils';
import {
  getCampaignParametersFromUrl,
  isObjectEmpty,
  serializeCampaignParameters,
} from '@/utils/campaignUtils';
import { createRedirectUrl } from '@/utils/createRedirectUrl';
import { metaRootPath } from '@/utils/metaUtils';
import { assertValidLang, getCurrency } from '@/utils/ssrUtils';
import { getLocale, i18nextMiddleware } from '~/middleware/i18next';

import { AwinMasterTag } from '../src/components/Awin/Awin';
import { ZendeskProvider } from '../src/components/Zendesk';
import { getOrigin } from '../src/server-only/origin';
import { setSessionId } from '../src/server-only/sessions';
import { getCMSRootConfig } from '../src/services/cms/PayloadEndpointServices';
import type { Route } from './+types/root';
import {
  isAppCompatibilityError,
  isClientSideAppCompatibilityError,
} from './errors';
import { fetchCurrentUserAttributesSSR } from './server/amplify';

// https://remix.run/docs/en/main/guides/envvars#browser-environment-variables
const getEnv = () => {
  const {
    REACT_APP_API_ENDPOINT_ECOMMERCE,
    REACT_APP_CMS_ENDPOINT,
    REACT_APP_CMS_MEDIA_ENDPOINT,
  } = process.env;

  return {
    REACT_APP_API_ENDPOINT_ECOMMERCE,
    REACT_APP_CMS_ENDPOINT,
    REACT_APP_CMS_MEDIA_ENDPOINT,
  };
};

export const unstable_middleware = [i18nextMiddleware];

export const loader = async ({
  request,
  params,
  context,
}: Route.LoaderArgs) => {
  const url = new URL(request.url);
  const locale = getLocale(context);

  const sessionId = uuidv4();
  Sentry.setTag(SLHeaders.SESSION_ID, sessionId);
  const serializedSession = await setSessionId(request, sessionId);

  const origin = await getOrigin({ request, params });

  // Root loader won't have the cookie already set at the request,
  // so we're setting it ourselves to match the majority of the cases
  // where we receive it through the Cookie header
  request.headers.append('Cookie', serializedSession);

  const env = getEnv();

  const [config, cmsRootConfig] = await Promise.all([
    getConfig({ sessionId, origin }),
    getCMSRootConfig({ sessionId, origin, locale: params.lang }),
  ]);

  const [tenant, seoDefaults, headerContent, footerContent, redirects] =
    cmsRootConfig;

  configureAmplify(config);
  setDefaultDateFnsLocale(locale as SUPPORTED_LOCALE);

  executeRedirects({ url, redirects, config, lang: params.lang });
  assertValidLang(params);

  const cognitoUser = await fetchCurrentUserAttributesSSR(
    sessionId,
    request,
    config,
  );

  const shopBrand = tenant.brand as ShopBrand;
  const brand = shopBrands.includes(shopBrand) ? shopBrand : 'simlocal';

  const loaderData = {
    env,
    config,
    locale,
    sessionId,
    user: cognitoUser?.userAttributes,
    currency: getCurrency({ request, config }),
    requestUrl: request.url,
    meta: seoDefaults?.meta ?? {},
    headerContent,
    footerContent,
    origin,
    brand,
  };

  const campaignParameters = getCampaignParametersFromUrl(request.url);

  const headers = new Headers();
  headers.append('Set-Cookie', serializedSession);

  if (!isObjectEmpty(campaignParameters)) {
    headers.append(
      'Set-Cookie',
      serializeCampaignParameters(
        campaignParameters,
        url.hostname,
        config.USER_ACTIVITY_TIMEOUT,
      ),
    );
  }

  return data(loaderData, { headers });
};

export const meta: Route.MetaFunction = metaRootPath;

const BrandFontLinks = ({ brand }: { brand: ShopBrand }) => {
  return (
    <>
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link
        rel="preconnect"
        href="https://fonts.gstatic.com"
        crossOrigin="anonymous"
      />
      <link rel="stylesheet" href={getBrandFontsUrl(brand)} />
    </>
  );
};

const getBrandStyles = (brand: ShopBrand) => {
  return Object.fromEntries(
    Object.entries(getBrandVariables(brand)).map(([property, value]) => [
      `--${property}`,
      value,
    ]),
  );
};

const executeRedirects = (args: {
  url: URL;
  redirects: Redirect[];
  config: Config;
  lang: string | undefined;
}) => {
  const createdRedirect = createRedirectUrl(args);
  if (createdRedirect) {
    throw redirect(createdRedirect.url, createdRedirect.httpStatus);
  }
};

export function Layout({ children }: { children: React.ReactNode }) {
  const data = useRouteLoaderData<typeof loader>('root');

  const { brand } = data ?? {};
  const brandStyles = brand ? getBrandStyles(brand) : {};

  const { i18n } = useTranslation();

  // This hook will change the i18n instance language to the current locale
  // detected by the loader, this way, when we do something to change the
  // language, this locale will change and i18next will load the correct
  // translation files

  return (
    <html
      lang={i18n.language}
      dir={i18n.dir(i18n.language)}
      style={{ ...brandStyles }}
    >
      <head>
        <meta charSet="utf-8" />
        {Capacitor.isNativePlatform() ? (
          <meta
            name="viewport"
            content="width=device-width, shrink-to-fit=no, initial-scale=1, maximum-scale=1, user-scalable=no, viewport-fit=cover"
          />
        ) : (
          <meta name="viewport" content="width=device-width, initial-scale=1" />
        )}
        <link rel="icon" href="/favicon.ico" sizes="32x32" />
        <link rel="icon" href="/favicon-32x32.png" type="image/png" />
        <link rel="apple-touch-icon" href="/apple-touch-icon.png" />
        {brand ? <BrandFontLinks brand={brand} /> : null}
        <meta name="theme-color" content="#ffffff" />
        {brand && isDefaultBrand(brand) ? (
          <>
            <meta
              name="google-site-verification"
              content="xjR_lsDUHWqMqztSyFHnyMFQyMIP4uP2IA5TQKT0cN8"
            />
            <meta
              name="facebook-domain-verification"
              content="h7colboe9sia4re2d0y4fezpfp8qvr"
            />
          </>
        ) : null}
        <Meta />
        <Links />
      </head>
      <body>
        {children}
        <div id="modal" />
        <ScrollRestoration />
        <script
          dangerouslySetInnerHTML={{
            __html: `window.ENV = ${JSON.stringify(data?.env)}`,
          }}
        />
        <Scripts />
      </body>
    </html>
  );
}

function App({ loaderData }: Route.ComponentProps) {
  const {
    config,
    sessionId,
    user,
    locale,
    currency,
    headerContent,
    footerContent,
    origin,
  } = loaderData;

  useChangeLanguage(locale);

  Sentry.setTag(SLHeaders.SESSION_ID, sessionId);

  useState(() => {
    if (typeof window !== 'undefined') {
      configureAmplify(config);
      setDefaultDateFnsLocale(locale as SUPPORTED_LOCALE);
    }
  });

  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            refetchOnWindowFocus: false,
            retry: false,
          },
        },
      }),
  );

  useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      SplashScreen.hide();
    }
  }, []);

  return (
    <SessionProvider sessionId={sessionId} origin={origin}>
      <HelmetProvider>
        <QueryClientProvider client={queryClient}>
          <MockConfigProvider config={config}>
            <DesignSystemProvider
              brand={config.WHITELABEL_BRAND}
              isApp={Capacitor.isNativePlatform()}
            >
              <LoadingBarProvider>
                <ConsentProvider>
                  <CurrencyProvider ssrCurrency={currency}>
                    <AnalyticsProvider>
                      <EuiccManager>
                        <UserProvider initCognitoUser={user ?? undefined}>
                          <CMSProvider
                            content={{
                              header: headerContent ?? undefined,
                              footer: footerContent ?? undefined,
                            }}
                          >
                            <ConnectionProvider>
                              <TikTokPixelTracker />
                              <MetaPixelTracker />
                              <ZendeskProvider>
                                <DiscountCodeManager />
                                <Outlet />
                              </ZendeskProvider>
                              <AwinMasterTag />
                            </ConnectionProvider>
                          </CMSProvider>
                        </UserProvider>
                      </EuiccManager>
                    </AnalyticsProvider>
                  </CurrencyProvider>
                </ConsentProvider>
              </LoadingBarProvider>
            </DesignSystemProvider>
          </MockConfigProvider>
        </QueryClientProvider>
      </HelmetProvider>
    </SessionProvider>
  );
}

export default App;

export function ErrorBoundary({ error }: Route.ErrorBoundaryProps) {
  if (isRouteErrorResponse(error) && error.status === 404) {
    return (
      <GlobalErrorScreen
        header={
          <Trans
            i18nKey="pageNotFound.404title"
            defaults={'404 - Oops! That didn’t go as planned.'}
          />
        }
        message={[
          <Trans
            key="pageNotFound.textFirst"
            i18nKey="pageNotFound.textFirst"
            defaults={'This page must’ve gone on holiday without telling us.'}
          />,
          <br key="brFirst" />,
          <Trans
            key="pageNotFound.textSecond"
            i18nKey="pageNotFound.textSecond"
            defaults={'Let’s head back to the homepage and get you sorted.'}
          />,
        ]}
        button={
          <Button
            data-testid="button-update-app"
            fluid
            onClick={() => {
              location.href = '/';
            }}
          >
            {
              <Trans
                i18nKey="error_page.back_to_home"
                defaults={'Back to Home'}
              />
            }
          </Button>
        }
      />
    );
  }

  if (
    isAppCompatibilityError(error) ||
    isClientSideAppCompatibilityError(error)
  ) {
    return <ApiCompatibilityErrorScreen />;
  }

  if (error instanceof Error) {
    Sentry.captureException(error);
    return <GlobalErrorBoundaryFallback errorData={{ error }} />;
  }

  return <GlobalErrorBoundaryFallback errorData={{ error: new Error() }} />;
}
